import { skipToken } from '@apollo/client/react'
import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import ModelItemDisplay from 'src/components/GenerationModel/ModelItemDisplay'
import type { ArtworkWithTaskDetailBaseFragment } from 'src/services/apollo/generated/graphql'
import { useGetGenerationModelByVersionIdForArtwork } from 'src/services/apollo/generation-model'
import { isModelStub } from 'src/utils/generationModel/modelPermissions'

type Props = {
  loras: ArtworkWithTaskDetailBaseFragment['loras']
  modelId?: string
}

export default function ArtworkDetailContentModels(props: Props) {
  // useGetGenerationModelByVersionIdForArtwork suspends on cache miss in Apollo 4.
  // Without this boundary, the suspension bubbles up to ArtworkDetailDialog's
  // <Suspense fallback={<LoadingPage />}>, showing a full-page loader.
  return (
    <Suspense>
      <ArtworkDetailContentModelsContent {...props} />
    </Suspense>
  )
}

function ArtworkDetailContentModelsContent(props: Props) {
  const {
    modelId,
    // Show skeletons if lora data is not ready.
    // The minimum number of items per row is 3. So we render 2 lora skeletons
    // (3 items = 1 model + 2 lora).
    loras = [undefined, undefined],
  } = props
  // oxfmt-ignore
  const { data: { model } = {}, loading: loadingModel } = useGetGenerationModelByVersionIdForArtwork(modelId ?? skipToken)

  const { t } = useTranslation(['artwork'])

  /**
   * Render
   */

  const modelNode = ((model && !isModelStub(model)) || loadingModel) && (
    <ModelItemDisplay model={model} />
  )
  const filteredLoras = loras.filter(m => m && !isModelStub(m))
  const loraNodes = filteredLoras.length
    ? filteredLoras.map((lora, idx) => (
        <ModelItemDisplay key={lora?.id || idx} model={lora} />
      ))
    : null

  return (
    <div className="flex flex-col gap-2">
      {modelNode}
      {loraNodes}
      {!modelNode && !loraNodes && (
        <div className="grid place-content-center text-sm bg-background-light text-primary-light rounded-xl h-[4.5rem]">
          {t('artwork:messages.no-model-info')}
        </div>
      )}
    </div>
  )
}
